import React, { useState, useMemo, useRef } from "react";
import styles from "./Hand2OffsetCalibrationComponent.module.css";
import MtiButton from "../../../Buttons/MtiButton";
import { useEffect } from "react";
import CalibrationApi from "../../../../api/CalibrationApi";
import useCalibrationLog, {
  CALIBRATION_LOG_KEY,
} from "../../../Hooks/useCalibrationLog";
import { Column } from "react-table";
import Hand2OffsetCalibrationResult from "../../../../Model/Hand2OffsetCalibrationResult";
import CommonTable from "../../../Common/CommonTable";
import LayoutWithLog from "../LayoutWithLog";
import { useTypedSelector } from "../../../../redux/reducers/RootReducer";
import { ESuiteMode, EPsrState } from "../../../../Model/ModuleState";
import { useDispatch } from "react-redux";
import { sendNotification } from "../../../../redux/actions/NotificationAction";
import { ENotificationType } from "../../../../Model/ENotificationType";
import { ECommonProcessState } from "../../../../Model/ECommonProcessState";
import { getModules } from "../../../../redux/reducers/ModuleReducer";
import { EModuleType } from "../../../../Model/EModuleType";
import { getModuleState } from "../../../../redux/reducers/ModuleStateReducer";
import {
  calibrationSignalRMap,
  calibrationTabMap,
} from "../SettingsStorageRi1000";
import { HubConnection, HubConnectionBuilder } from "@aspnet/signalr";
import config from "../../../../config";

interface Props {
  setOnApply: (onApply: { execute: () => void; disabled: boolean }) => void;
  setOnCancel: (onCancel: { execute: () => void; disabled: boolean }) => void;
  setResetHandler: (onReset: {
    execute: () => void;
    disabled: boolean;
  }) => void;
  setIsCalibrationUpdated: (isUpdated: boolean) => void;
  setRunningTabKey: React.Dispatch<React.SetStateAction<string>>;
}

function roundToTwoDecimal(num: number) {
  return Math.round((num + Number.EPSILON) * 100) / 100;
}

export default function Hand2OffsetCalibrationComponent({
  setOnApply,
  setOnCancel,
  setIsCalibrationUpdated,
  setResetHandler,
  setRunningTabKey,
}: Props) {
  const dispatch = useDispatch();
  const [processState, setProcessState] = useState(ECommonProcessState.Initial);
  const [allowRunCalibration, setAllowRunCalibration] = useState(true);
  const suiteState = useTypedSelector(
    (state) => state.moduleStateStore.moduleStateById["Suite"]?.suite
  );
  const suiteMode = suiteState?.suiteMode ?? ESuiteMode.Error;
  const psrModules = useTypedSelector((state) =>
    getModules(state, EModuleType.PSR)
  );
  const psrIdentifier = psrModules?.[0]?.identifier;
  const psrState = useTypedSelector(
    (state) => getModuleState(state, psrIdentifier).psr
  );

  // GET LOG LOGIC
  const [logs] = useCalibrationLog(calibrationSignalRMap.hand2Offset);

  useEffect(() => {
    if (suiteMode == ESuiteMode.AutoCalibrating) {
      setProcessState(ECommonProcessState.Processing);
      setAllowRunCalibration(false);

      return;
    }

    if (suiteMode == ESuiteMode.Normal) {
      if (processState === ECommonProcessState.Processing) {
        if (psrState?.state === EPsrState.Fail) {
          setProcessState(ECommonProcessState.Fault);
          setRunningTabKey("");
        } else if (psrState?.state === EPsrState.Available) {
          setProcessState(ECommonProcessState.Finished);
          setRunningTabKey("");
        }
      }
      setAllowRunCalibration(true);
      return;
    }

    if (suiteMode == ESuiteMode.ArmCrashed || suiteMode == ESuiteMode.Error) {
      setProcessState(ECommonProcessState.Initial);
      return;
    }
  }, [suiteMode, suiteState]);

  const runCalibration = () => {
    setRunningTabKey(calibrationSignalRMap.hand2Offset);
    CalibrationApi.runHand2OffsetCalibration().then(() => {
      setProcessState(ECommonProcessState.Started);
    });
  };

  const stopCalibration = () => CalibrationApi.stopHand2OffsetCalibration();

  const onApply = () => {
    CalibrationApi.saveHand2OffsetCalibration()
      .then(() => {
        dispatch(
          sendNotification(
            ENotificationType.Success,
            "The changes have been saved successfully"
          )
        );
        setCalibrationState(false);
      })
      .catch(() => {
        dispatch(
          sendNotification(
            ENotificationType.Error,
            "Saving changes encountered an error"
          )
        );
      });
  };

  const onCancel = () => {
    CalibrationApi.revertHand2OffsetCalibration().then(() => {
      setCalibrationState(false);
      CalibrationApi.getHand2OffsetCalibraionResult().then((result) => {
        if (result.length > 0) {
          setCorrectionResultData(result);
        } else {
          setCorrectionResultData([] as Hand2OffsetCalibrationResult[]);
        }
      });
    });
  };

  useEffect(() => {
    setResetHandler({
      execute: () => {
        setCalibrationState(false);
        CalibrationApi.getHand2OffsetCalibraionResult().then((result) => {
          if (result.length > 0) {
            setCorrectionResultData(result);
          } else {
            setCorrectionResultData([] as Hand2OffsetCalibrationResult[]);
          }
        });
      },
      disabled: false,
    });
  }, []);

  const setCalibrationState = (isUpdated: boolean) => {
    setOnApply({
      execute: onApply,
      disabled: !isUpdated,
    });
    setOnCancel({
      execute: onCancel,
      disabled: !isUpdated,
    });
    setIsCalibrationUpdated(isUpdated);
  };

  const [correctionResultData, setCorrectionResultData] = useState(
    [] as Hand2OffsetCalibrationResult[]
  );
  const resultColumn: Column<Hand2OffsetCalibrationResult>[] = useMemo(
    () =>
      [
        {
          id: "hotel",
          Header: "Hotel",
          accessor: (row: Hand2OffsetCalibrationResult) => row.hotel,
        },
        {
          id: "slot",
          Header: "Slot",
          accessor: (row: Hand2OffsetCalibrationResult) => row.slot,
        },
        {
          id: "hand-1-theta",
          Header: "Hand 1 Theta (deg)",
          accessor: (row: Hand2OffsetCalibrationResult) =>
            roundToTwoDecimal(row.hand1Theta),
        },
        {
          id: "hand-2-theta",
          Header: "Hand 2 Theta (deg)",
          accessor: (row: Hand2OffsetCalibrationResult) =>
            roundToTwoDecimal(row.hand2Theta),
        },
        {
          id: "hand-2-theta-offset",
          Header: "Hand 2 Correction (deg)",
          accessor: (row: Hand2OffsetCalibrationResult) =>
            roundToTwoDecimal(row.hand2ThetaOffset),
        },
        {
          id: "hand-1-extends",
          Header: "Hand 1 Extends (mm)",
          accessor: (row: Hand2OffsetCalibrationResult) =>
            roundToTwoDecimal(row.hand1Extends),
        },
        {
          id: "hand-2-extends",
          Header: "Hand 2 Extends (mm)",
          accessor: (row: Hand2OffsetCalibrationResult) =>
            roundToTwoDecimal(row.hand2Extends),
        },
        {
          id: "hand-2-extends-offset",
          Header: "Hand 2 Extends Offset (mm)",
          accessor: (row: Hand2OffsetCalibrationResult) =>
            roundToTwoDecimal(row.hand2ExtendsOffset),
        },
        {
          id: "hand-1-z",
          Header: "Hand 1 Z (mm)",
          accessor: (row: Hand2OffsetCalibrationResult) =>
            roundToTwoDecimal(row.hand1Z),
        },
        {
          id: "hand-2-z",
          Header: "Hand 2 Z (mm)",
          accessor: (row: Hand2OffsetCalibrationResult) =>
            roundToTwoDecimal(row.hand2Z),
        },
        {
          id: "hand-2-z-offset",
          Header: "Hand 2 Z Offset (mm)",
          accessor: (row: Hand2OffsetCalibrationResult) =>
            roundToTwoDecimal(row.hand2ZOffset),
        },
      ] as Column<Hand2OffsetCalibrationResult>[],
    []
  );

  useEffect(() => {
    switch (processState) {
      case ECommonProcessState.Finished:
        CalibrationApi.getHand2OffsetCalibraionResult().then((result) => {
          if (result.length > 0) {
            setCorrectionResultData(result);
          } else {
            setCorrectionResultData([] as Hand2OffsetCalibrationResult[]);
          }
          setProcessState(ECommonProcessState.Initial);
          setCalibrationState(true);
        });
        break;
      default:
        break;
    }
  }, [processState]);

  useEffect(() => {
    CalibrationApi.getHand2OffsetCalibraionResult().then((result) => {
      if (result.length > 0) {
        setCorrectionResultData(result);
      } else {
        setCorrectionResultData([] as Hand2OffsetCalibrationResult[]);
      }
    });
  }, []);

  return (
    <div className={styles["outer-border"]}>
      <LayoutWithLog logMessages={logs ?? []}>
        <LayoutWithLog.Content>
          <div className={styles["calibration-view"]}>
            <div className={styles["calibration-control"]}>
              <div className={styles["label-child"]}>
                Calibrating Hand 2 Offset
              </div>
              <div className={styles["button-group"]}>
                <MtiButton
                  variant="secondary"
                  colorVariant="black"
                  onClick={stopCalibration}
                  disabled={allowRunCalibration}
                >
                  STOP
                </MtiButton>
                <MtiButton
                  variant="primary"
                  colorVariant="black"
                  onClick={runCalibration}
                  disabled={!allowRunCalibration}
                >
                  RUN
                </MtiButton>
              </div>
            </div>

            <div className={styles["result-table-view"]}>
              <CommonTable<Hand2OffsetCalibrationResult>
                data={correctionResultData}
                columns={resultColumn}
                style={{
                  width: "100%",
                  height: "fit-content",
                  padding: 0,
                }}
                variant="no-separator"
                enableFilter={false}
                enablePagination={true}
                enableSelection={false}
                enableMultiSelect={false}
                enableManualSorting={false}
              />
            </div>
          </div>
        </LayoutWithLog.Content>
      </LayoutWithLog>
    </div>
  );
}
